/**
 * AdminHomePage - Admin console overview
 *
 * Shows quick links and status summary.
 */

import { Link } from 'react-router-dom';
import { Cpu, BarChart3, Settings, ExternalLink } from 'lucide-react';
import { AdminLayout } from './AdminLayout';

const QUICK_LINKS = [
  {
    path: '/admin/ai-tagging',
    label: 'AI Tagging',
    description: 'Run AI tagging jobs for movies and TV shows',
    icon: Cpu,
    color: 'bg-purple-500/10 text-purple-400',
  },
  {
    path: '/admin/completeness',
    label: 'Content Completeness',
    description: 'View missing data stats and query missing content',
    icon: BarChart3,
    color: 'bg-teal-500/10 text-teal-400',
  },
  {
    path: '/admin/config',
    label: 'Recommendation Config',
    description: 'View and edit recommendation system configuration',
    icon: Settings,
    color: 'bg-orange-500/10 text-orange-400',
  },
];

export function AdminHomePage() {
  return (
    <AdminLayout title="Overview" description="Admin console for Filmple operators">
      {/* Quick Links Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {QUICK_LINKS.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.path}
              to={link.path}
              className="group p-6 bg-[#1A1F26] rounded-xl border border-white/[0.08] hover:border-white/[0.15] transition-all"
            >
              <div className={`inline-flex p-3 rounded-lg ${link.color} mb-4`}>
                <Icon size={24} />
              </div>
              <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-teal-400 transition-colors">
                {link.label}
              </h3>
              <p className="text-sm text-slate-400">{link.description}</p>
            </Link>
          );
        })}
      </div>

      {/* Safety Notice */}
      <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
        <h4 className="font-medium text-orange-400 mb-2">Safety Notice</h4>
        <p className="text-sm text-slate-300">
          This admin console has 150% safety gating:
        </p>
        <ul className="text-sm text-slate-400 mt-2 space-y-1">
          <li>• IP allowlist (network layer)</li>
          <li>• Staff JWT role (auth layer)</li>
          <li>• Explicit user ID allowlist (defense-in-depth)</li>
        </ul>
      </div>

      {/* Documentation Links */}
      <div className="mt-8">
        <h3 className="text-lg font-semibold text-white mb-4">Documentation</h3>
        <div className="space-y-2">
          <a
            href="https://github.com/Filmple/Filmple/blob/main/docs/canonical/ADMIN_CONSOLE_CANONICAL.md"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors"
          >
            <ExternalLink size={14} />
            Admin Console Canonical Spec
          </a>
          <a
            href="https://github.com/Filmple/Filmple/blob/main/docs/canonical/RECOMMENDATION_SYSTEM_CANONICAL.md"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-teal-400 transition-colors"
          >
            <ExternalLink size={14} />
            Recommendation System Canonical Spec
          </a>
        </div>
      </div>
    </AdminLayout>
  );
}
